import { MapStyleSelector } from "@/components/map/style-selector";
import { ProjectionSelector } from "@/components/map/projection-selector";
import { useStore } from "@/store/store";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { initAudioContext } from "@/lib/audio";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { ThemeSelector } from "@/components/ui/theme-selector";

export const PreferencesRoute = () => {
  const alertsEnabled = useStore((state) => state.alertsEnabled);
  const toggleAlerts = useStore((state) => state.toggleAlerts);
  const shouldShowAircraft = useStore((state) => state.shouldShowAircraft);
  const setShouldShowAircraft = useStore(
    (state) => state.setShouldShowAircraft
  );
  const shouldShowFlightRestrictions = useStore(
    (state) => state.shouldShowFlightRestrictions
  );
  const setShouldShowFlightRestrictions = useStore(
    (state) => state.setShouldShowFlightRestrictions
  );

  const handleToggleAlerts = () => {
    // Initialize audio context when enabling alerts
    if (!alertsEnabled) {
      initAudioContext();
    }
    toggleAlerts();
  };

  return (
    <main className="min-h-screen bg-background">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Preferences", href: "/preferences" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-1">Preferences</h1>
          <p className="text-muted-foreground">
            Customize your application experience
          </p>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-1">Theme</div>
            <div className="font-medium">System</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-1">Map Style</div>
            <div className="font-medium">Satellite</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-1">Alerts</div>
            <div className="font-medium">{alertsEnabled ? "On" : "Off"}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-muted-foreground mb-1">Layers</div>
            <div className="font-medium">Custom</div>
          </div>
        </div>

         {/* Settings Sections */}
         <div className="space-y-6">
           {/* Appearance */}
           <div>
             <h2 className="text-lg font-medium mb-4">Appearance</h2>
             <div className="space-y-3">
               <div className="flex items-center justify-between py-2 ">
                 <div>
                   <Label htmlFor="theme" className="font-medium">
                     Theme
                   </Label>
                   <p className="text-sm text-muted-foreground">
                     Choose your preferred color scheme
                   </p>
                 </div>
                 <ThemeSelector />
               </div>
             </div>
           </div>

           <Separator />

          {/* Map */}
          <div>
            <h2 className="text-lg font-medium mb-4">Map</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 ">
                <div>
                  <Label htmlFor="map-style" className="font-medium">
                    Map Style
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Select your preferred map visualization
                  </p>
                </div>
                <MapStyleSelector />
              </div>
              <div className="flex items-center justify-between py-2 ">
                <div>
                  <Label htmlFor="map-projection" className="font-medium">
                    Map Projection
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Choose between flat or globe view
                  </p>
                </div>
                <ProjectionSelector />
              </div>
            </div>
          </div>

           <Separator />

           {/* Map Layers */}
           <div>
             <h2 className="text-lg font-medium mb-4">Map Layers</h2>
             <div className="space-y-3">
               <div className="flex items-center justify-between py-2 ">
                 <div>
                   <Label htmlFor="should-show-aircraft" className="font-medium">
                     Aircraft Markers
                   </Label>
                   <p className="text-sm text-muted-foreground">
                     Show aircraft positions on the map
                   </p>
                 </div>
                 <Switch
                   id="should-show-aircraft"
                   checked={shouldShowAircraft}
                   onCheckedChange={() =>
                     setShouldShowAircraft(!shouldShowAircraft)
                   }
                 />
               </div>
               <div className="flex items-center justify-between py-2">
                 <div>
                   <Label
                     htmlFor="should-show-flight-restrictions"
                     className="font-medium"
                   >
                     Flight Restrictions
                   </Label>
                   <p className="text-sm text-muted-foreground">
                     Display restricted airspace zones
                   </p>
                 </div>
                 <Switch
                   id="should-show-flight-restrictions"
                   checked={shouldShowFlightRestrictions}
                   onCheckedChange={() =>
                     setShouldShowFlightRestrictions(
                       !shouldShowFlightRestrictions
                     )
                   }
                 />
               </div>
             </div>
           </div>

           <Separator />

           {/* Notifications */}
           <div>
             <h2 className="text-lg font-medium mb-4">Notifications</h2>
             <div className="space-y-3">
               <div className="flex items-center justify-between py-2 ">
                 <div>
                   <Label htmlFor="audio-alerts" className="font-medium">
                     Audio Alerts
                   </Label>
                   <p className="text-sm text-muted-foreground">
                     Play sounds when new aircraft are detected
                   </p>
                 </div>
                 <Switch
                   id="audio-alerts"
                   checked={alertsEnabled}
                   onCheckedChange={handleToggleAlerts}
                 />
               </div>
             </div>
           </div>
         </div>
      </div>
    </main>
  );
};
